// import api's and mutations
import {
  ContactType,
  getContacts,
  saveContact,
  updateContact,
  deleteContact
} from 'RestAPIs/contacts/contacts';

// import components
import Button from '@sdb/ui/atoms/Button';
import { Box } from '@mui/material';
import CaoSection from './CaoSection';
import ContactCard from './ContactCard';
import ContactDialog, { ProcessedContact } from './ContactDialog';
import ContactsStyles from './ContactsStyles';
import {
  GridContainerFlex,
  GridCompact3,
  GridCompact8
} from 'components/shared/Grid/Grid.components';
import { StyledBoxFlex } from 'components/shared/Box/Box.styles';
import { StyledTypographyTruncate } from 'components/shared/Typography/Typography.styles';

// import hooks
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// import recoil
import { RecoilRoot } from 'recoil';

// import types
import { Contact } from './types';

// import utils
import { omit } from 'lodash';

function Contacts() {
  const { t } = useTranslation('common');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [type, setType] = useState<ContactType>();
  const [showContactDialog, setShowContactDialog] = useState(false);
  const contactTypes = [
    ContactType.APPLICATION_MANAGER,
    ContactType.APPLICATION_OWNER,
    ContactType.ROSTER_PROCESS_MANAGER,
    ContactType.FLEX_PROCESS_MANAGER,
    ContactType.ICT_MANAGER,
    ContactType.DATABASE_VIEWS_API_INTEGRATIONS,
    ContactType.EXPORT_SALARY_ADMINISTRATOR,
    ContactType.EXPORT_FINANCIAL,
    ContactType.SECURITY_OFFICER
  ];

  const onContactSaved = (contact: ProcessedContact) => {
    if (contact.id) {
      updateContact(contact.id, {
        ...omit(contact, ['id']),
        type: contact.type as ContactType
      }).then(({ data }) => {
        const existingContacts = contacts.filter((c) => c.id !== contact.id);
        setContacts([...existingContacts, data]);
      });
    } else {
      saveContact({
        ...omit(contact, ['id']),
        type: contact.type as ContactType
      }).then(({ data }) => {
        setContacts([...contacts, data]);
      });
    }
  };

  const onContactDelete = (id: number) => {
    deleteContact(id).then(() => {
      setContacts(contacts.filter((c) => c.id !== id));
    });
  };

  useEffect(() => {
    getContacts().then(({ data }) => {
      setContacts(data);
    });
  }, []);

  return (
    <Box>
      <CaoSection />
      <ContactsStyles>
        <StyledBoxFlex gap={2}>
          <Box
            sx={{
              flexGrow: 1
            }}
          >
            {contactTypes.map((contactType) => {
              const contactsByType = contacts.filter(
                ({ type }) => contactType === type
              );
              return (
                <GridContainerFlex>
                  <GridCompact3>
                    <StyledTypographyTruncate variant="body2">
                      {t(`contactTypes.${contactType}`)}
                    </StyledTypographyTruncate>
                  </GridCompact3>
                  <GridCompact8>
                    {contactsByType.map((c) => (
                      <ContactCard
                        key={c.id}
                        {...c}
                        onContactSaved={onContactSaved}
                        onDeleteContact={onContactDelete}
                      />
                    ))}
                    {!contactsByType.length && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setType(contactType);
                          setShowContactDialog(true);
                        }}
                      >
                        {t('addContact')}
                      </Button>
                    )}
                  </GridCompact8>
                </GridContainerFlex>
              );
            })}
          </Box>
          <Box>
            <Button
              variant="contained"
              onClick={() => {
                setType(undefined);
                setShowContactDialog(true);
              }}
            >
              {t('addContact')}
            </Button>
          </Box>
        </StyledBoxFlex>
        {showContactDialog && (
          <ContactDialog
            type={type}
            onSave={onContactSaved}
            onHidden={() => setShowContactDialog(false)}
          />
        )}
      </ContactsStyles>
    </Box>
  );
}

export default function ContactsRoot() {
  return (
    <RecoilRoot>
      <Contacts />
    </RecoilRoot>
  );
}
